import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes, useLocation} from 'react-router-dom';
import {isTokenValid, resolveTenant, setCookie} from './api';
import ConnectionBanner from './components/ConnectionBanner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MyCourses from './pages/MyCourses';
import MyDocuments from './pages/MyDocuments';
import CourseViewer from './pages/CourseViewer';
import Timesheets from './pages/Timesheets';
import TimesheetDetail from './pages/TimesheetDetail';
import MyCredentials from './pages/MyCredentials';
import MyProfile from './pages/MyProfile';
import MyTimeOff from './pages/MyTimeOff';
import NotificationSettings from './pages/NotificationSettings';
import Policies from './pages/Policies';
import Feed from './pages/Feed';
import Benefits from './pages/Benefits';
import Expenses from './pages/Expenses';
import Goals from './pages/Goals';
import TeamCalendar from './pages/TeamCalendar';
import PayHistory from './pages/PayHistory';
import NotFound from './pages/NotFound';
import ErrorBoundary from './components/ErrorBoundary';

function TenantGate({ children }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    resolveTenant().then(tenant => {
      if (tenant) {
        setCookie('tenantId', tenant.id);
        setCookie('tenantName', encodeURIComponent(tenant.name));
        setReady(true);
      } else {
        setError(true);
      }
    });
  }, []);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Organization Not Found</h1>
        <p className="text-gray-500">This subdomain is not associated with any organization.</p>
      </div>
    </div>
  );

  if (!ready) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-gray-500">Loading...</div>
    </div>
  );

  return children;
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  return isTokenValid() ? children : <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
}

export default function App() {
  return (
    <Router>
      <ErrorBoundary>
      <ConnectionBanner />
      <TenantGate>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/courses" element={<ProtectedRoute><MyCourses /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><MyDocuments /></ProtectedRoute>} />
          <Route path="/courses/:courseId" element={<ProtectedRoute><CourseViewer /></ProtectedRoute>} />
          <Route path="/timesheets" element={<ProtectedRoute><Timesheets /></ProtectedRoute>} />
          <Route path="/timesheets/:id" element={<ProtectedRoute><TimesheetDetail /></ProtectedRoute>} />
          <Route path="/credentials" element={<ProtectedRoute><MyCredentials /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
          <Route path="/time-off" element={<ProtectedRoute><MyTimeOff /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/policies" element={<ProtectedRoute><Policies /></ProtectedRoute>} />
          <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
          <Route path="/benefits" element={<ProtectedRoute><Benefits /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
          <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
          <Route path="/team-calendar" element={<ProtectedRoute><TeamCalendar /></ProtectedRoute>} />
          <Route path="/pay-history" element={<ProtectedRoute><PayHistory /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </TenantGate>
      </ErrorBoundary>
    </Router>
  );
}
